import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, UserPlus, User } from 'lucide-react';
import { toast } from 'sonner';

export default function UserManagement({ users, refreshUsers }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    language: 'en',
    timezone: 'UTC',
    location: '',
    religion_module: 'none'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        modules: {
          reminders: true,
          emergency: true,
          prayer_times: formData.religion_module === 'islam',
          religious_stories: formData.religion_module === 'islam',
          video_calls: false,
          weather: false
        }
      };

      await axios.post(`${API}/users`, payload);
      toast.success('User profile created successfully!');
      await refreshUsers();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user profile');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="max-w-3xl mx-auto">
        <Button
          data-testid="back-btn"
          variant="outline"
          onClick={() => navigate('/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              <UserPlus className="w-6 h-6 inline-block mr-2" />
              Create User Profile
            </CardTitle>
            <CardDescription>
              Set up a profile for a senior user to get started
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  data-testid="input-name"
                  id="name"
                  type="text"
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="age">Age</Label>
                <Input
                  data-testid="input-age"
                  id="age"
                  type="number"
                  placeholder="Enter age"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="language">Language</Label>
                <Select
                  value={formData.language}
                  onValueChange={(value) => setFormData({ ...formData, language: value })}
                >
                  <SelectTrigger data-testid="select-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="ar">Arabic</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="tr">Turkish</SelectItem>
                    <SelectItem value="ur">Urdu</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="location">Location (City)</Label>
                <Input
                  data-testid="input-location"
                  id="location"
                  type="text"
                  placeholder="e.g., London, Dubai, Berlin"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Used for prayer times and weather</p>
              </div>

              <div>
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={formData.timezone}
                  onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                >
                  <SelectTrigger data-testid="select-timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="Europe/London">London</SelectItem>
                    <SelectItem value="Europe/Berlin">Berlin</SelectItem>
                    <SelectItem value="Asia/Dubai">Dubai</SelectItem>
                    <SelectItem value="Asia/Karachi">Karachi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="religion">Religious Module</Label>
                <Select
                  value={formData.religion_module}
                  onValueChange={(value) => setFormData({ ...formData, religion_module: value })}
                >
                  <SelectTrigger data-testid="select-religion">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="islam">Islamic (Prayer Times & Quran)</SelectItem>
                    <SelectItem value="christian">Christian (Coming Soon)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Enable religious features like prayer times and spiritual content
                </p>
              </div>

              <Button 
                data-testid="submit-user-btn"
                type="submit" 
                className="w-full"
              >
                Create Profile
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Existing Users */}
        {users.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Existing Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {users.map((user) => (
                  <div
                    key={user.id}
                    data-testid={`user-item-${user.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">
                          {user.age ? `${user.age} years old` : 'Age not specified'}
                        </p>
                      </div>
                    </div>
                    <Button
                      data-testid={`view-user-btn-${user.id}`}
                      size="sm"
                      onClick={() => navigate('/dashboard')}
                    >
                      View
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}